import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

interface SkillGap {
  skill: string;
  current_level: number;
  required_level: number;
  gap_percentage: number;
  priority: 'high' | 'medium' | 'low';
  months_to_close?: number;
}

interface SkillGapChartProps {
  gaps: SkillGap[];
  title?: string;
}

const SkillGapChart: React.FC<SkillGapChartProps> = ({
  gaps,
  title = 'Skill Gap Analysis - Development Priorities'
}) => {
  const sortedGaps = [...gaps].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return b.gap_percentage - a.gap_percentage;
  }).slice(0, 8);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityDot = (priority: string) => {
    const color = getPriorityColor(priority);
    return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }}></span>;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-2 text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 mb-6">
        Identify and prioritize skill development areas to achieve your career goals
      </p>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={sortedGaps}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={true} vertical={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
            label={{ value: 'Skill Level (%)', position: 'insideBottom', offset: -5, style: { fill: '#6b7280', fontSize: 12 } }}
          />
          <YAxis
            type="category"
            dataKey="skill"
            tick={{ fill: '#374151', fontSize: 12 }}
            width={115}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              padding: '12px'
            }}
            formatter={(value: number, name: string) => {
              if (name === 'current_level') return [`${value}%`, 'Current Level'];
              if (name === 'required_level') return [`${value}%`, 'Required Level'];
              return [value, name];
            }}
            cursor={{ fill: '#f9fafb' }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '10px' }}
            formatter={(value) => {
              if (value === 'current_level') return 'Current';
              if (value === 'required_level') return 'Required';
              return value;
            }}
          />
          <Bar dataKey="current_level" fill="#0891b2" radius={[0, 4, 4, 0]} name="current_level" />
          <Bar dataKey="required_level" radius={[0, 4, 4, 0]} name="required_level">
            {sortedGaps.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getPriorityColor(entry.priority)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {sortedGaps.slice(0, 4).map((gap, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900 leading-tight">{gap.skill}</h4>
              {getPriorityDot(gap.priority)}
            </div>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>Gap:</span>
                <span className="font-semibold text-gray-900">{gap.gap_percentage}%</span>
              </div>
              {gap.months_to_close && (
                <div className="flex justify-between">
                  <span>Timeline:</span>
                  <span className="font-semibold text-gray-900">{gap.months_to_close} months</span>
                </div>
              )}
              <div className="pt-1 border-t border-gray-200 mt-2">
                <span className="capitalize text-xs font-medium" style={{ color: getPriorityColor(gap.priority) }}>
                  {gap.priority} Priority
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span>High Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span>Medium Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span>Low Priority</span>
        </div>
      </div>

      <div className="mt-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
        <p className="text-sm font-medium text-amber-900 mb-1">Development Focus</p>
        <p className="text-xs text-amber-700">
          Prioritize high-priority skills with the largest gaps. Based on your current trajectory,
          focus on {sortedGaps.filter(g => g.priority === 'high').length} high-priority areas
          for maximum career impact.
        </p>
      </div>
    </div>
  );
};

export default SkillGapChart;
